import {createSlice, PayloadAction}			from '@reduxjs/toolkit';

import {RootState}							from '../../app/store';
import {BasketState, BasketPayload,
	BasketAddressPayload, BasketTracking,
	BasketSetPayload}						from './interface';
import {User}								from '../user/interface';

import {ft_getDeviceCatalogPrice}			from '../../ressources/functions/deviceFunction';
import {getLocalStorage, setLocalStorage}	from '../../ressources/functions/localStorage';

import * as API								from '../../ressources/API';


const initialState: BasketState = (getLocalStorage('basket')?.data ?
	{...getLocalStorage('basket'), open: false}
	:
	{
		data:        [],
		price:       0,
		quantity:    0,
		open:        false
	}
);

function ft_findDevice(state: BasketState, action: PayloadAction<BasketPayload | BasketAddressPayload>) {
	const deviceId = 'device' in action.payload ? action.payload.device._id : action.payload.deviceId;
	const index = state.data.findIndex((find) => {
		return (
			find.device._id === deviceId
			&& find.duration === action.payload.duration
			&& find.keyboard === action.payload.keyboard
		);
	});

	return ({
		index,
		data: state.data[index]
	});
}

function saveBasket(state: BasketState, user: User) {
	const trackingState: BasketTracking['data'] = state.data.map(data => ({
		...data,
		device: data.device._id
	}));

	API.putCartTracking({
		price:    state.price,
		data:     trackingState,
		quantity: state.quantity
	}, user);

	setLocalStorage('basket', state);
}

export const settingsReducer = createSlice({
	name:     'settings',
	initialState,
	reducers: {
		addToBasket: (state, action: PayloadAction<BasketPayload>) => {
			const findDevice = ft_findDevice(state, action).data;

			if (findDevice) {
				findDevice.quantity += action.payload.quantity;

				if (!findDevice.address)
					findDevice.address = [];
				if (!findDevice.assign)
					findDevice.assign = [];
				if (!findDevice.link)
					findDevice.link = Array(findDevice.quantity - action.payload.quantity).fill('');

				for (let i = 0; i < action.payload.quantity; i++) {
					findDevice.address.push(action.payload.addressId || '');
					findDevice.assign.push(action.payload.assign || '');
					findDevice.link.push(action.payload.link?.[i] || '');
				}
			} else {
				state.data.push({
					device:   action.payload.device,
					quantity: action.payload.quantity,
					keyboard: action.payload.keyboard,
					duration: action.payload.duration,
					assign:   Array(action.payload.quantity).fill(action.payload.assign || ''),
					address:  Array(action.payload.quantity).fill(action.payload.addressId || ''),
					link:     Array.from({length: action.payload.quantity}, (_, i) => action.payload.link?.[i] || '')
				});
			}

			const price = ft_getDeviceCatalogPrice(action.payload.device, action.payload.duration);

			state.quantity += action.payload.quantity;
			state.price += price * action.payload.quantity;

			saveBasket(state, action.payload.user);
		},
		removeToBasket: (state, action: PayloadAction<BasketPayload>) => {
			const findDeviceIndex = ft_findDevice(state, action).index;

			if (findDeviceIndex === -1)
				return;

			const entry = state.data[findDeviceIndex];

			if (!entry.address)
				entry.address = [];
			if (!entry.assign)
				entry.assign = [];
			if (!entry.link)
				entry.link = [];
			while (entry.link.length < entry.quantity)
				entry.link.push('');

			const start = entry.quantity - action.payload.quantity;
			const removedLinks = entry.link.slice(start).filter(Boolean);
			const isDevice = !!entry.device.category.needCharger;

			entry.quantity -= action.payload.quantity;
			entry.address.splice(start, action.payload.quantity);
			entry.assign.splice(start, action.payload.quantity);
			entry.link.splice(start, action.payload.quantity);
			state.quantity -= action.payload.quantity;
			state.price -= ft_getDeviceCatalogPrice(entry.device, action.payload.duration);

			if (removedLinks.length) {
				state.data.forEach((data) => {
					if (!data.link)
						return;

					for (let i = data.link.length - 1; i >= 0; i--) {
						if (!removedLinks.includes(data.link[i]))
							continue;

						if (isDevice) {
							data.quantity--;
							data.address.splice(i, 1);
							data.assign.splice(i, 1);
							data.link.splice(i, 1);
							state.quantity--;
							state.price -= ft_getDeviceCatalogPrice(data.device, data.duration);
						} else
							data.link[i] = '';
					}
				});
			}

			state.data = state.data.filter(data => data.quantity > 0);

			if (state.data.length === 0) {
				state.price = 0;
				state.quantity = 0;
			}

			saveBasket(state, action.payload.user);
		},
		basketChangeAddress: (state, action: PayloadAction<BasketAddressPayload>) => {
			const findDevice = ft_findDevice(state, action).data;

			if (findDevice && findDevice.address.length - 1 >= action.payload.index) {
				findDevice.address[action.payload.index] = action.payload.newId;

				saveBasket(state, action.payload.user);
			}
		},
		basketChangeAssign: (state, action: PayloadAction<BasketAddressPayload>) => {
			const findDevice = ft_findDevice(state, action).data;

			if (findDevice && findDevice.assign.length - 1 >= action.payload.index) {
				findDevice.assign[action.payload.index] = action.payload.newId;

				saveBasket(state, action.payload.user);
			}
		},
		basketChangeLink: (state, action: PayloadAction<BasketAddressPayload>) => {
			const findDevice = ft_findDevice(state, action).data;

			if (findDevice && findDevice.quantity > action.payload.index) {
				if (!findDevice.link)
					findDevice.link = [];
				while (findDevice.link.length < findDevice.quantity)
					findDevice.link.push('');

				findDevice.link[action.payload.index] = action.payload.newId;
				saveBasket(state, action.payload.user);
			}
		},
		resetBasket: (state, action: PayloadAction<User>) => {
			state.data = [];
			state.price = 0;
			state.quantity = 0;
			saveBasket(state, action.payload);
		},
		openBasket: (state) => {
			state.open = true;
		},
		closeBasket: (state) => {
			state.open = false;
		},
		setBasket: (state, action: PayloadAction<BasketSetPayload>) => {
			state.data = action.payload.data;
			state.price = action.payload.price;
			saveBasket(state, action.payload.user);
		},
		setFullBasket: (state, action: PayloadAction<BasketSetPayload>) => {
			state.data = action.payload.data;
			state.price = action.payload.price;
			state.quantity = action.payload.quantity;
			state.addressList = action.payload.addressList;
			state.open = false;

			saveBasket(state, action.payload.user);
		}
	}
});

export const {addToBasket, removeToBasket,
	basketChangeAddress, basketChangeAssign,
	resetBasket, openBasket, closeBasket,
	setBasket, setFullBasket, basketChangeLink} = settingsReducer.actions;

export const basket = (state: RootState) => state.basket;

export default settingsReducer.reducer;
